import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { PageBody, backLinkVariants } from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { OrganisationForm } from '@/components/organisations/OrganisationForm.tsx';
import { OrganisationLookup } from '@/components/organisations/OrganisationLookup.tsx';
import { OrganisationSuggestionNotes } from '@/components/organisations/OrganisationSuggestionNotes.tsx';
import { ReadingColumn } from '@/components/ReadingColumn.tsx';
import type { OrganisationSuggestion } from '@/lib/organisations.ts';

export interface OrganisationCreateSearch {
  /** Comes from the Slack alert about a Teams install from an unknown tenant. */
  msteams_tenant_id?: string;
}

interface OrganisationCreateSearchProps {
  msteamsTenantId?: string;
}

export const Route = createFileRoute('/_app/organisations/create')({
  staticData: { orgScope: 'none' },
  validateSearch: (search: Record<string, unknown>): OrganisationCreateSearch => ({
    msteams_tenant_id:
      typeof search.msteams_tenant_id === 'string' ? search.msteams_tenant_id : undefined,
  }),
  component: CreateOrganisationRoute,
});

function CreateOrganisationRoute() {
  const { msteams_tenant_id: msteamsTenantId } = Route.useSearch();

  return <CreateOrganisationPage msteamsTenantId={msteamsTenantId} />;
}

/* The lookup comes first; "manual" skips it and opens the empty form. */
type Step =
  | { kind: 'lookup' }
  | { kind: 'manual' }
  | { kind: 'suggested'; suggestion: OrganisationSuggestion };

export function CreateOrganisationPage({
  msteamsTenantId,
}: OrganisationCreateSearchProps) {
  const [step, setStep] = useState<Step>({ kind: msteamsTenantId ? 'manual' : 'lookup' });

  return (
    <>
      <Link to='/organisations' className={backLinkVariants({ className: 'mb-2.5' })}>
        <ChevronLeft aria-hidden='true' />
        Organisations
      </Link>
      <PageHeader title='Create Organisation' subtitle='Create a new organisation' />

      {/* The back-link has taken the space PageBody normally leaves for it. */}
      <PageBody flush className='mt-7'>
        <ReadingColumn>
          {step.kind === 'lookup' && (
            <OrganisationLookup
              onFound={(suggestion) => setStep({ kind: 'suggested', suggestion })}
              onManual={() => setStep({ kind: 'manual' })}
            />
          )}
          {step.kind === 'manual' && (
            <OrganisationForm prefill={{ msteams_tenant_id: msteamsTenantId ?? '' }} />
          )}
          {step.kind === 'suggested' && (
            <div className='flex flex-col gap-5'>
              <OrganisationSuggestionNotes
                suggestion={step.suggestion}
                onStartOver={() => setStep({ kind: 'lookup' })}
              />
              <OrganisationForm
                prefill={{
                  name: step.suggestion.name,
                  primary_domain: step.suggestion.domain,
                  msteams_tenant_id: step.suggestion.msteams_tenant_id ?? '',
                  internal_domains: step.suggestion.internal_domains,
                }}
              />
            </div>
          )}
        </ReadingColumn>
      </PageBody>
    </>
  );
}
